import React, { useRef, useEffect, useState, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import { useSalaryBand } from "../../contexts/SalaryBandContext";
import Modal from "../../components/UI/Modal";
import SalaryGradeForm from "../../components/Form/SalaryGradeForm";
import ReusableTable from "../../components/UI/ReusableTable";
import DeleteButton from "../../components/UI/AlertDialog";
import Notification from "../../components/UI/Notification";
import { Description } from "@headlessui/react";
import { X, Briefcase, MoreVertical, Building, Calendar, Users, MapPin, Award, Clock, Grid, Search } from 'lucide-react';
import DropdownMenu from "../../components/UI/DropdownMenu";
import StatusBadge from "../../components/UI/StatusBadge";

const SalaryGradeMasters = React.memo(() => {
  const { salaryBands, addSalaryBand, updateSalaryBand, deleteSalaryBand, isLoading } = useSalaryBand();
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSalaryGrade, setEditingSalaryGrade] = useState(null);
  const [message, setMessage] = useState(""); // Success message
  const [formErrors, setFormErrors] = useState({}); // Validation errors
  const [notification, setNotification] = useState(null);
  const [openMenu, setOpenMenu] = useState(false);
  const [isClient, setIsClient] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Separate component for actions column to avoid hooks rule violation
  const ActionsColumn = React.memo(({ row, openMenu, setOpenMenu, openModalForEdit, handleDeleteApproverModule }) => {
    const buttonRef = useRef(null);
    
    return (
      <div className="flex items-center justify-center relative">
        <button
          ref={buttonRef}
          onClick={() =>
            setOpenMenu(openMenu === row.id ? null : row.id)
          }
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
        >
          <MoreVertical className="w-4 h-4 text-gray-600 dark:text-gray-400" />
        </button>

        {openMenu === row.id && (
          <DropdownMenu
            buttonRef={buttonRef}
            onClose={() => setOpenMenu(null)}
          >
            <button
              onClick={(e) => {
                openModalForEdit(row);
                setOpenMenu(null);
              }}
              className="w-full text-left px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
            >
              Edit
            </button>
            <DeleteButton
              style={`text-red-600 hover:text-red-800 px-3 py-2`}
              onDelete={(e) => {
                handleDeleteApproverModule(row.id);
                setOpenMenu(null);
              }}
            />
          </DropdownMenu>
        )}
      </div>
    );
  });

  useEffect(() => {
    setIsClient(true); // Ensures document.body is available
  }, []);

  // Handlers
  const openModalForAdd = useCallback(() => {
    setEditingSalaryGrade(null);
    setIsModalOpen(true);
    setMessage("");
    setFormErrors({});
  }, []);

  const openModalForEdit = useCallback((salaryGrade) => {
    setEditingSalaryGrade(salaryGrade);
    setIsModalOpen(true);
    setMessage("");
    setFormErrors({});
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setEditingSalaryGrade(null);
    setFormErrors({});
    setMessage("");
  }, []);

  const convertFormDataToObject = (formData) => {
    return Object.fromEntries(formData.entries());
  };

  // create and update function
  const handleFormSubmit = useCallback(
    async (salaryGrade) => {
      setLoading(true);
      try {
        if (editingSalaryGrade) {
          // console.log("workplace payload", salaryGrade);
        
          // Convert FormData to object
          // console.log(salaryGrade instanceof FormData)
          const salaryGradeData = salaryGrade instanceof FormData ? convertFormDataToObject(salaryGrade) : salaryGrade;
  
          // console.log("Payload being sent:", { ...editingSalaryGrade, ...salaryGradeData });
  
          await updateSalaryBand({ ...editingSalaryGrade, ...salaryGradeData });
          setMessage("Salary grade updated successfully.");
          // setNotification("salary grade updated successfully.");
          setNotification({
            message: "salary Grade updated successfully.",
            type: "success",
          });
        } else {
          // console.log("create salary grade", salaryGrade)
          await addSalaryBand(salaryGrade);
          setMessage("salaryGrade added successfully.");
          // setNotification("salaryGrade added successfully.");
          setNotification({
            message: "salaryGrade added successfully.",
            type: "success",
          });
        }
        closeModal();
      } catch (err) {
        if (err.errors) {
          setFormErrors(err.errors);
        } else {
          // console.error("Unexpected error during submission:", err);
          // setNotification("Unexpected error during submission:", err);
          setNotification({
            message: "Unexpected error during submission:", err,
            type: "error",
          });
        }
      } finally {
        setLoading(false);
      }
    },
    [editingSalaryGrade, addSalaryBand, updateSalaryBand, closeModal]
  );

  // Memoize department names
  const salaryGradeForTable = useMemo(
    () =>
        salaryBands.map((salaryGrade) => ({
        ...salaryGrade,
        name: salaryGrade?.name || "Unknown Salary Grade",
      })),
    [salaryBands]
  );

  const filteredSalaryGrades = useMemo(() => {
    return salaryGradeForTable.filter((item) => {
      const searchTerm = searchQuery.toLowerCase();
      return item.name.toLowerCase().includes(searchTerm);
    });
  }, [salaryGradeForTable, searchQuery]);

  // delete function
  const handleDeleteSalaryGrade = async (id) => {
    try {
      await deleteSalaryBand(id);
      // setNotification("Salary grade deleted successfully");
      setNotification({
        message: "Salary Grade deleted successfully",
        type: "success",
      });
    } catch (error) {
      // setNotification("Failed to delete salary grade");
      setNotification({
        message: "Failed to delete salary grade",
        type: "error",
      });
    }
  };

  // Table columns
  const columns = useMemo(
    () => [
      { label: "Name", field: "name" },
      {
        label: "Status",
        field: "is_active",
        render: (row) => <StatusBadge status={row.is_active} />,
      },
      {
        label: "Actions",
        field: "actions",
        render: (row) => (
          <ActionsColumn
            row={row}
            openMenu={openMenu}
            setOpenMenu={setOpenMenu}
            openModalForEdit={openModalForEdit}
            handleDeleteApproverModule={handleDeleteSalaryGrade}
          />
        ),
      },
    ],
    [
      openMenu,
      openModalForEdit,
      handleDeleteSalaryGrade,
    ]
  );
  

  return (
    <div className="px-3 py-2 bg-white dark:bg-gray-900 font-poppins rounded-lg">
      
      {/* notification compo */}
      {notification && (
        <Notification message={notification.message} type={notification.type} onClose={() => setNotification(null)} />
      )}

      <div className="mb-6">
        <h2 className="text-2xl font-semibold text-black dark:text-white">Salary Grade Masters</h2>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        {/* Left End: Search Bar */}
        <div className="relative w-full sm:max-w-xl"> 
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-10 py-2 border border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all font-poppins"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Right End: Add Button */}
        <button
          onClick={openModalForAdd}
          className="bg-blue-600 font-medium text-white px-4 py-2 rounded-md hover:bg-blue-700 whitespace-nowrap"
        >
          Add Salary Grade
        </button>
      </div>

      {/* Success Message */}
      {message && (
        <div className="mb-4 p-4 bg-green-100 border border-green-500 text-green-700 rounded-md">
          {message}
        </div>
      )}

      {/* Candidates Table */}
      <ReusableTable
        data={filteredSalaryGrades}
        loading={isLoading}
        columns={columns}
        filterable={false}
        excludeFilterColumns={["actions"]}
      />

      {/* Modal for Adding or Editing */}
      <Modal
        isOpen={isModalOpen}
        onRequestClose={closeModal}
        title={editingSalaryGrade ? "Edit Salary Grade" : "Add Salary Grade"}
      >
        <SalaryGradeForm
          loading={loading}
          closeModal={closeModal}
          onSubmit={handleFormSubmit}
          initialValues={editingSalaryGrade || { name: "", description: "", is_active: "" }}
          errors={formErrors}
        />
      </Modal>
    </div>
  );
});

export default SalaryGradeMasters;
